import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../../hooks/app";
import {
  AppAvatar,
  AppBox,
  AppButton,
  AppModal,
  AppPaper,
  AppSkeletonHeader,
  AppTitle,
} from "../../../../commons/components";
import { StatusEnum } from "../../../../commons/enums/status-enum";
import { IUser } from "../../../../commons/models/i-user";
import setDocumentTitle from "../../../../commons/utilities/document-title";
import { toTitleCase } from "../../../../commons/utilities/string-utils";
import { Can } from "../../../../casl/Can";
import { RolePolicyActionEnum } from "../../../../commons/enums/role-policy-action.enum";
import { User } from "../../../../commons/models/user";
import { RoleNameEnum } from "../../../../commons/enums/role-name.enum";
import IconQuestionCircle from "../../../../commons/components/icons/question-circle";
import UserAdminAssignUnassign from "./UserAdminAssingUnassign";
import { userAssignAsAdmin } from "../../../../redux/slices/user/manage-user-admin-assign-slice";
import { userUnassignAsAdmin } from "../../../../redux/slices/user/manage-user-admin-unassign-slice";
import { useSnackbar } from "../../../../redux/snackbarProvider";
import { fetchUser } from "../../../../redux/slices/user/manage-user-slice";
import { AxiosError } from "axios";
import { IServerErrorResponse } from "../../../../commons/components/interface";
import useAuth from "../../../../hooks/useAuth";
import { useNavigate } from "react-router";

interface UserManageDetailHeaderProps {
  user: IUser;
  onEditUser?: (event?: Event) => void;
}

export default function UserManageDetailHeader({ user, onEditUser }: UserManageDetailHeaderProps) {
  const dispatch = useAppDispatch();
  const addSnackbar = useSnackbar();
  const { user: authUser } = useAuth();
  const { userInfoStatus } = useAppSelector((state) => state.manageUser);
  const { userAssignAsAdminStatus } = useAppSelector((state) => state.manageUserAssignAsAdmin);
  const { userUnassignAsAdminStatus } = useAppSelector((state) => state.manageUserUnassignAsAdmin);
  const [isAdmin, setAdmin] = useState<boolean>();
  const [isAdminToggle, setAdminToggle] = useState<boolean>(false);
  useEffect(() => {
    setDocumentTitle([`${toTitleCase(user.fullName)}`, "Manage"]);
    if (user.roles.length > 0) {
      const adminRole = user.roles.find((role) => role.name === RoleNameEnum.ADMIN);
      if (adminRole) {
        setAdmin(true);
      } else {
        setAdmin(false);
      }
    } else {
      setAdmin(false);
    }
  }, [user]);

  const handleAdminToggleConfirm = async (e: Event) => {
    e.preventDefault();
    const action = isAdmin ? userUnassignAsAdmin(user.id) : userAssignAsAdmin(user.id);
    const successKey = isAdmin ? "user-admin-unassign-success" : "user-admin-assign-success";
    const successMessage = `User ${isAdmin ? "Unassigned" : "Assigned"} as Admin Successfully`;
    try {
      await dispatch(action).unwrap();
      addSnackbar({
        key: successKey,
        text: successMessage,
        variant: "success",
      });
      setAdminToggle(false);
      //   const isCurrentUser = user.id === authUser?.id;
      //   if (isCurrentUser) {
      //     await dispatch(authUserProfile()).unwrap();
      //     if (ability.cannot(RolePolicyActionEnum.READ, User)) {
      //       navigate("/manage");
      //       return;
      //     }
      //   }
      dispatch(fetchUser(user.id));
    } catch (error) {
      const err = error as AxiosError<IServerErrorResponse>;
      addSnackbar({
        key: "error",
        text: err.response?.data.message || "Something went wrong",
        variant: "danger",
      });
    }
  };
  return (
    <>
      <AppPaper shadow="xs" padding="none">
        {userInfoStatus === StatusEnum.Loading ? (
          <AppSkeletonHeader withoutBackground />
        ) : (
          <AppBox alignItems="center" justifyContent="space-between" p="sm" gap="xs">
            <AppBox alignItems="center" gap="xs">
              <AppAvatar username={user.fullName} size="2xl" src={user.avatar || ""} />
              <AppBox flexDirection="column" gap="3xs">
                <AppTitle as="h4">{toTitleCase(user.fullName)}</AppTitle>
              </AppBox>
            </AppBox>
            <AppBox gap="2xs">
              {user.id !== authUser?.id && (
                <Can I={RolePolicyActionEnum.CREATE} a={User}>
                  <AppButton
                    variant="light"
                    label={isAdmin ? "Unassign as Admin" : "Assign as Admin"}
                    onClick={() => setAdminToggle(true)}
                  />
                </Can>
              )}
              <Can I={RolePolicyActionEnum.UPDATE} a={User}>
                <AppButton variant="filled" label="Edit User Info" onClick={onEditUser} />
              </Can>
            </AppBox>
          </AppBox>
        )}
      </AppPaper>
      <AppModal
        opened={isAdminToggle}
        title={isAdmin ? "Want To Unassign As Admin?" : "Want To Assign As Admin?"}
        titleIcon={<IconQuestionCircle />}
        iconColor="info"
        withLoadingButton={
          userAssignAsAdminStatus === StatusEnum.Loading || userUnassignAsAdminStatus === StatusEnum.Loading
        }
        onClose={() => {
          setAdminToggle(false);
        }}
        onSave={(e) => {
          handleAdminToggleConfirm(e as Event);
        }}
      >
        <UserAdminAssignUnassign user={user} isAdminAssign={!isAdmin} />
      </AppModal>
    </>
  );
}
